import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiGetAuth, apiPatchAuth, isApiConfigured, ApiError } from "@/services/http";
import { isPremiumPlan } from "@/utils/plans";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PendingBusiness {
  id:           number;
  name:         string;
  slug:         string;
  categoryName: string;
  districtName: string;
  townName:     string | null;
  ownerName:    string;
  ownerEmail:   string;
  phone:        string | null;
  planSlug:     string;
  submittedAt:  string;
  status:       "pending" | "approved" | "rejected";
  videoUrl?:    string | null;
  notes?:       string;
}

// ── Demo data ─────────────────────────────────────────────────────────────────

const DEMO_QUEUE: PendingBusiness[] = [
  {
    id: 201, name: "Kamath Timber & Hardware", slug: "kamath-timber-hardware",
    categoryName: "Hardware & Construction", districtName: "Dakshina Kannada",
    townName: "Bantwal", ownerName: "Rajesh Kamath", ownerEmail: "rkamath@timber.in",
    phone: "+91 98449 12345", planSlug: "business", submittedAt: "2026-09-12T08:22:00Z", status: "pending",
    notes: "Owner submitted photos of shopfront. GST document pending.",
  },
  {
    id: 202, name: "Udupi Sri Krishna Sweets", slug: "udupi-sri-krishna-sweets",
    categoryName: "Sweets & Bakeries", districtName: "Udupi",
    townName: "Udupi", ownerName: "Anand Prabhu", ownerEmail: "anand@krishnasweets.in",
    phone: "+91 97410 88321", planSlug: "free", submittedAt: "2026-09-11T14:10:00Z", status: "pending",
    notes: "",
  },
  {
    id: 203, name: "Coorg Plantation Resort", slug: "coorg-plantation-resort",
    categoryName: "Hotels & Resorts", districtName: "Dakshina Kannada",
    townName: "Puttur", ownerName: "Suresh Aiyappa", ownerEmail: "resort@coorgplantation.com",
    phone: "+91 94489 55678", planSlug: "premium", submittedAt: "2026-09-10T10:55:00Z", status: "pending",
    videoUrl: "https://www.youtube.com/watch?v=ScMzIvxBSi4",
    notes: "Premium plan — priority review requested.",
  },
  {
    id: 204, name: "Mangalore Fresh Fish Hub", slug: "mangalore-fresh-fish-hub",
    categoryName: "Seafood & Fish Market", districtName: "Dakshina Kannada",
    townName: "Mangaluru", ownerName: "Thomas D'Souza", ownerEmail: "thomas@freshfish.in",
    phone: "+91 90088 44312", planSlug: "business", submittedAt: "2026-09-09T09:30:00Z", status: "pending",
    notes: "",
  },
  {
    id: 205, name: "Kundapur Ayurveda Centre", slug: "kundapur-ayurveda-centre",
    categoryName: "Health & Wellness", districtName: "Udupi",
    townName: "Kundapur", ownerName: "Dr. Madhavi Shenoy", ownerEmail: "dr.shenoy@ayurveda.in",
    phone: "+91 96321 77654", planSlug: "free", submittedAt: "2026-09-08T15:40:00Z", status: "pending",
    notes: "AYUSH license submitted.",
  },
  {
    id: 206, name: "Kumta Beach Home Stay", slug: "kumta-beach-home-stay",
    categoryName: "Home Stays & Resorts", districtName: "Uttara Kannada",
    townName: "Kumta", ownerName: "Pradeep Naik", ownerEmail: "pradeep@kumtastay.in",
    phone: "+91 98441 33200", planSlug: "business", submittedAt: "2026-09-07T11:20:00Z", status: "approved",
    notes: "Approved — photos verified.",
  },
  {
    id: 207, name: "Sirsi Coffee Estate Stay", slug: "sirsi-coffee-estate-stay",
    categoryName: "Home Stays & Resorts", districtName: "Uttara Kannada",
    townName: "Sirsi", ownerName: "Ganesh Hegde", ownerEmail: "ganesh@sirsicoffee.in",
    phone: "+91 94806 22114", planSlug: "premium", submittedAt: "2026-09-13T09:15:00Z", status: "pending",
    notes: "Premium plan — video tour not attached yet.",
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const d = Math.floor(diff / 86400000);
  if (d < 1) return "Today";
  if (d === 1) return "Yesterday";
  return `${d}d ago`;
}

const PLAN_COLORS: Record<string, string> = {
  free:     "bg-background-200 text-foreground-600",
  business: "bg-primary-100 text-primary-700",
  premium:  "bg-accent-100 text-accent-700",
};

const STATUS_COLORS: Record<string, string> = {
  pending:  "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function ApprovalQueue() {
  const { accessToken } = useAuth();
  const [listings, setListings]   = useState<PendingBusiness[]>([]);
  const [loading, setLoading]     = useState(true);
  const [actionId, setActionId]   = useState<number | null>(null);
  const [filter, setFilter]       = useState<"all" | "pending" | "approved" | "rejected">("pending");
  const [expandId, setExpandId]   = useState<number | null>(null);
  const [error, setError]         = useState("");

  useEffect(() => {
    if (!isApiConfigured() || !accessToken) {
      setTimeout(() => { setListings(DEMO_QUEUE); setLoading(false); }, 350);
      return;
    }
    apiGetAuth<{ data: PendingBusiness[] }>("/admin/businesses/queue", accessToken)
      .then((res) => setListings(res.data ?? []))
      .catch(() => setListings(DEMO_QUEUE))
      .finally(() => setLoading(false));
  }, [accessToken]);

  async function handleAction(id: number, action: "approve" | "reject") {
    setActionId(id);
    setError("");
    try {
      if (!isApiConfigured() || !accessToken) {
        await new Promise((r) => setTimeout(r, 600));
      } else {
        await apiPatchAuth(`/admin/businesses/${id}/${action}`, {}, accessToken);
      }
      setListings((prev) =>
        prev.map((l) =>
          l.id === id
            ? { ...l, status: action === "approve" ? "approved" : "rejected" }
            : l,
        ),
      );
      setExpandId(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Action failed. Please retry.");
    } finally {
      setActionId(null);
    }
  }

  const displayed = listings.filter((l) => filter === "all" || l.status === filter);
  const pending   = listings.filter((l) => l.status === "pending").length;

  const filterTabs: { key: typeof filter; label: string }[] = [
    { key: "pending",  label: `Pending (${pending})` },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "all",      label: "All" },
  ];

  return (
    <div>
      {/* Stats row */}
      <div className="mb-6 grid grid-cols-3 gap-4">
        {[
          { label: "Awaiting Review",  value: pending,                                          color: "text-amber-600",   bg: "bg-amber-50",    icon: "ri-time-line" },
          { label: "Approved Today",   value: listings.filter((l) => l.status === "approved").length, color: "text-emerald-600", bg: "bg-emerald-50",  icon: "ri-checkbox-circle-line" },
          { label: "Rejected",         value: listings.filter((l) => l.status === "rejected").length, color: "text-rose-600",    bg: "bg-rose-50",     icon: "ri-close-circle-line" },
        ].map((s) => (
          <div key={s.label} className="flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-5">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${s.bg}`}>
              <i className={`${s.icon} text-xl ${s.color}`} />
            </div>
            <div>
              <p className="font-heading text-2xl font-bold text-foreground-950">{loading ? "—" : s.value}</p>
              <p className="text-xs text-foreground-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Table card */}
      <div className="rounded-xl border border-background-200 bg-background-50">
        <div className="flex items-center justify-between border-b border-background-200 px-6 py-4">
          <h2 className="font-heading text-base font-bold text-foreground-950">Submitted Listings</h2>
          {pending > 0 && (
            <span className="rounded-full bg-amber-100 px-3 py-0.5 text-xs font-bold text-amber-700">
              {pending} need review
            </span>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 overflow-x-auto border-b border-background-200 px-5 pt-3">
          {filterTabs.map((t) => (
            <button
              key={t.key} type="button" onClick={() => setFilter(t.key)}
              className={`cursor-pointer whitespace-nowrap border-b-2 px-3 pb-2.5 text-xs font-semibold transition-colors ${
                filter === t.key
                  ? "border-primary-500 text-primary-700"
                  : "border-transparent text-foreground-500 hover:text-foreground-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mx-6 mt-4 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <i className="ri-error-warning-line mr-2" />{error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : displayed.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-list-check-3 text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No listings in this filter</p>
          </div>
        ) : (
          <div className="divide-y divide-background-100">
            {displayed.map((item) => (
              <div key={item.id}>
                <button
                  type="button"
                  onClick={() => setExpandId(expandId === item.id ? null : item.id)}
                  className="flex w-full cursor-pointer items-start gap-4 px-6 py-4 text-left transition-colors hover:bg-background-50"
                >
                  {/* Icon */}
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-background-200 bg-background-100">
                    <i className="ri-store-2-line text-base text-foreground-500" />
                  </div>

                  {/* Main info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-foreground-950">{item.name}</p>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${PLAN_COLORS[item.planSlug] ?? PLAN_COLORS.free}`}>
                        {item.planSlug}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${STATUS_COLORS[item.status]}`}>
                        {item.status}
                      </span>
                      {isPremiumPlan(item.planSlug) && (
                        item.videoUrl ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                            <i className="ri-video-line text-[11px]" /> Video added
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                            <i className="ri-video-line text-[11px]" /> Video missing
                          </span>
                        )
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-foreground-500">
                      {item.categoryName} · {item.townName ?? item.districtName} · {item.ownerName}
                    </p>
                  </div>

                  {/* Date + chevron */}
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <p className="text-[11px] text-foreground-400">{timeAgo(item.submittedAt)}</p>
                    <i className={`text-sm text-foreground-400 ${expandId === item.id ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
                  </div>
                </button>

                {/* Expanded detail */}
                {expandId === item.id && (
                  <div className="mx-6 mb-4 rounded-lg border border-background-200 bg-background-50 p-5">
                    <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                      <div className="space-y-2">
                        <p className="text-foreground-700">
                          <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">Business</span>
                          <br />{item.name}
                        </p>
                        <p className="text-foreground-700">
                          <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">Category</span>
                          <br />{item.categoryName}
                        </p>
                        <p className="text-foreground-700">
                          <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">Location</span>
                          <br />{item.townName ?? item.districtName}, {item.districtName}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-foreground-700">
                          <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">Owner</span>
                          <br />{item.ownerName}
                        </p>
                        <a href={`mailto:${item.ownerEmail}`} className="block text-primary-600 hover:underline">
                          {item.ownerEmail}
                        </a>
                        {item.phone && (
                          <a href={`tel:${item.phone}`} className="block text-primary-600 hover:underline">
                            {item.phone}
                          </a>
                        )}
                      </div>
                    </div>

                    {item.notes && (
                      <div className="mt-4 rounded-lg bg-background-100 px-4 py-3 text-xs text-foreground-600">
                        <p className="mb-1 font-semibold uppercase tracking-wider text-foreground-500">Review Notes</p>
                        {item.notes}
                      </div>
                    )}

                    {/* Video tour submission */}
                    <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
                      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-background-200 text-foreground-600">
                        <i className="ri-video-line text-sm" />
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">Video tour</span>
                      {item.videoUrl ? (
                        <a
                          href={item.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:underline"
                        >
                          <i className="ri-external-link-line text-sm" /> Open submitted link
                        </a>
                      ) : (
                        <span className="text-xs text-foreground-500">Not provided</span>
                      )}
                      {!isPremiumPlan(item.planSlug) && (
                        <span className="rounded-full bg-background-200 px-2 py-0.5 text-[10px] font-semibold text-foreground-500">
                          Premium feature
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    {item.status === "pending" && (
                      <div className="mt-4 flex gap-2">
                        <button
                          type="button"
                          disabled={actionId === item.id}
                          onClick={() => handleAction(item.id, "approve")}
                          className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white whitespace-nowrap transition-colors hover:bg-emerald-700 disabled:opacity-60"
                        >
                          {actionId === item.id
                            ? <i className="ri-loader-4-line animate-spin text-sm" />
                            : <i className="ri-checkbox-circle-line text-sm" />}
                          Approve &amp; Publish
                        </button>
                        <button
                          type="button"
                          disabled={actionId === item.id}
                          onClick={() => handleAction(item.id, "reject")}
                          className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-700 whitespace-nowrap transition-colors hover:bg-rose-100 disabled:opacity-60"
                        >
                          <i className="ri-close-circle-line text-sm" />
                          Reject
                        </button>
                        <a
                          href={`/business/${item.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-xs font-semibold text-foreground-700 whitespace-nowrap transition-colors hover:bg-background-100"
                        >
                          <i className="ri-external-link-line text-sm" />
                          Preview
                        </a>
                      </div>
                    )}
                    {item.status !== "pending" && (
                      <p className={`mt-4 text-xs font-semibold capitalize ${item.status === "approved" ? "text-emerald-700" : "text-rose-700"}`}>
                        <i className={`mr-1 ${item.status === "approved" ? "ri-checkbox-circle-line" : "ri-close-circle-line"}`} />
                        This listing has been {item.status}.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}